import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import { updateUserAttributes } from 'aws-amplify/auth';
import _ from 'lodash';
import * as userActions from '../../store/actions/user';
import { getOnboardingStepPath, getPostCollectDataNextPath, isKycOrLaterOnboardingStep, scrollOnboardingToTop } from '../../utils/onboardingUtils';
import DatePicker from '../../components/DatePicker';
import ErrorMessage from '../../components/ErrorMessage';
import PlacesAutocomplete from '../../components/NewAddressAutocomplete';
import SearchableSelect from '../../components/SearchableSelect';
import { DocumentReviewIcon, ReviewRow, ReviewSection, ReviewStepShell } from '../../components/OnboardingReview';
import { MapPinIcon, UserIcon } from '../../assets/icons';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';

const inputClass =
  'h-[43px] w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12] disabled:cursor-not-allowed disabled:opacity-50';
const inputErrorBorderClass = '!border-[#C0392B] border-2';
const searchableSelectTriggerClass = (hasError = false) =>
  `${inputClass} flex items-center gap-2 text-left ${hasError ? inputErrorBorderClass : ''}`;
const fieldLabelClass = 'mb-1.5 block text-[11.5px] font-semibold normal-case tracking-normal text-[#7A9AB0]';
const fieldErrorClass = 'mt-1 text-xs text-[#C0392B]';
const fieldGroupClass = 'mb-4';
const sectionHeaderClass =
  'mb-3 mt-[22px] flex items-center gap-1.5 border-b border-[#EEF3F8] pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#7A9AB0]/70';

function getAdultBirthDateLimit() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatBirthDateForDisplay(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return value || '';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

function omitEmptyStrings(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ''));
}

function CollectDataIndividual() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const formikRef = useRef();
  const hasSavedRef = useRef(false);
  const hasJustConfirmedRef = useRef(false);
  const {
    user,
    onboardingStatus,
    updatingIndividualCollectData,
    updateIndividualCollectDataError,
    advancingOnboardingStep,
    advanceOnboardingStepError,
    updateUserNamesError,
    updateUserError,
  } = useSelector((state) => state.user);
  const allowedOnboardingCountries = useSelector((state) => state.config?.allowedOnboardingCountries ?? []);
  const addressAutocompletePrimaryTypes = useMemo(() => ['street_address', 'route', 'premise', 'subpremise'], []);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryValues, setSummaryValues] = useState(null);
  const [cognitoNamesError, setCognitoNamesError] = useState(null);
  const [addressSearch, setAddressSearch] = useState('');
  const [showMiddleName, setShowMiddleName] = useState(false);
  const [showAddressLine2, setShowAddressLine2] = useState(false);

  const countryOptions = useMemo(() => {
    if (!Array.isArray(allowedOnboardingCountries)) return [];
    return allowedOnboardingCountries
      .map((country) => ({
        code: (country?.code ?? '').toUpperCase(),
        name: country?.name ?? country?.code ?? '',
      }))
      .filter((country) => country.code);
  }, [allowedOnboardingCountries]);

  const countrySelectOptions = useMemo(
    () =>
      countryOptions.map((country) => ({
        value: country.code,
        label: country.name,
        searchText: `${country.name} ${country.code}`,
      })),
    [countryOptions],
  );

  const isCollectDataStepCompleted = useMemo(() => {
    if (!Array.isArray(onboardingStatus?.steps)) return false;
    return onboardingStatus.steps.some(
      (s) => typeof s === 'object' && s !== null && (s?.step === 'CollectData' || s?.Step === 'CollectData') && s?.isCompleted === true,
    );
  }, [onboardingStatus?.steps]);

  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);
  const isLocked = isKycOrLaterStep;
  const maxBirthDate = getAdultBirthDateLimit();

  const updateNamesInCognito = async (firstName, lastName) => {
    await updateUserAttributes({
      userAttributes: {
        given_name: firstName,
        family_name: lastName,
      },
    });
  };

  const onAddressSelect = (addressData, setFieldValue, validateForm) => {
    const fullAddress = (addressData?.fullAddress || '').trim();
    setAddressSearch(fullAddress);
    const components = addressData?.components;
    if (components) {
      const streetLine = [components.route, components.streetNumber].filter(Boolean).join(' ').trim();
      setFieldValue('line1', streetLine || fullAddress, true);
      setFieldValue('city', components.city || '', true);
      setFieldValue('stateRegionProvince', components.state || '', true);
      setFieldValue('country', components.country ? components.country.toUpperCase() : '', true);
      setFieldValue('postCode', components.postalCode || '', true);
      validateForm?.();
    }
  };

  useEffect(() => {
    if (user && formikRef.current) {
      formikRef.current.setFieldValue('firstName', user.firstName ?? '', false);
      formikRef.current.setFieldValue('lastName', user.lastName ?? '', false);
      formikRef.current.setFieldValue('email', user.email ?? '', false);
      formikRef.current.setFieldValue('phoneNumber', user.phoneNumber ?? user?.userDetails?.phoneNumber ?? '', false);
    }
    if (user?.userDetails && formikRef.current) {
      formikRef.current.setFieldValue('middleName', user.userDetails.middleName ?? '', false);
      formikRef.current.setFieldValue('birthDate', user.userDetails.birthDate ?? '', false);
      if (user.userDetails.middleName) setShowMiddleName(true);
    }
    if (user?.userDetails?.address && formikRef.current) {
      formikRef.current.setFieldValue('line1', user.userDetails.address.line1 ?? '', false);
      formikRef.current.setFieldValue('line2', user.userDetails.address.line2 ?? '', false);
      formikRef.current.setFieldValue('city', user.userDetails.address.city ?? '', false);
      formikRef.current.setFieldValue('stateRegionProvince', user.userDetails.address.stateRegionProvince ?? '', false);
      formikRef.current.setFieldValue('postCode', user.userDetails.address.postCode ?? '', false);
      formikRef.current.setFieldValue('country', user.userDetails.address.country ?? '', false);
      const a = user.userDetails.address;
      const summary = [(a.line1 || '').trim(), [a.postCode, a.city].filter(Boolean).join(' ').trim(), (a.country || '').trim()]
        .filter(Boolean)
        .join(', ');
      if (summary) setAddressSearch(summary);
      if (user.userDetails.address.line2) setShowAddressLine2(true);
    }
  }, [user]);

  const schema = yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    middleName: yup.string().notRequired(),
    birthDate: yup
      .string()
      .required('Date of birth is required')
      .test('is-at-least-18', 'You must be at least 18 years old', (value) => !value || value <= maxBirthDate),
    line1: yup.string().required('Street address is required'),
    line2: yup.string().notRequired(),
    city: yup.string().required('City is required'),
    stateRegionProvince: yup.string().required('State / Region / Province is required'),
    postCode: yup.string().required('Post code is required'),
    country: yup.string().required('Country is required'),
  });

  const handleSubmit = async (values) => {
    setCognitoNamesError(null);
    setSummaryValues(values);

    if (isLocked) {
      setShowSummary(true);
      return;
    }

    const nameChanged = (user?.firstName !== values.firstName || user?.lastName !== values.lastName) && values.firstName && values.lastName;
    if (nameChanged) {
      try {
        await updateNamesInCognito(values.firstName, values.lastName);
      } catch (e) {
        setCognitoNamesError(e?.message || e?.detail || 'Failed to update name in session. Please try again.');
        return;
      }
    }

    const userDetailsFields = ['middleName', 'birthDate'];
    const userDetailsUpdated = _.pick(values, userDetailsFields);
    if (userDetailsUpdated.middleName === '') {
      userDetailsUpdated.middleName = null;
    }
    const userDetailsCurrent = _.pick(user?.userDetails, userDetailsFields);
    const userDetailsHasChanges = Object.keys(userDetailsUpdated).some(
      (k) => String(userDetailsUpdated[k] ?? '') !== String(userDetailsCurrent[k] ?? ''),
    );

    const addressFields = ['line1', 'line2', 'city', 'stateRegionProvince', 'postCode', 'country'];
    const addressUpdated = _.pick(values, addressFields);
    if (addressUpdated.line2 === '') {
      addressUpdated.line2 = null;
    }
    const addressCurrent = _.pick(user?.userDetails?.address, addressFields);
    const addressHasChanges = Object.keys(addressUpdated).some((k) => String(addressUpdated[k] ?? '') !== String(addressCurrent[k] ?? ''));

    const namesPayload = nameChanged
      ? {
          id: user?.id,
          email: user?.email,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: user?.phoneNumber ?? user?.userDetails?.phoneNumber,
          userType: user?.userType,
          isOnboarding: user?.isOnboarding,
        }
      : undefined;

    const detailsPayload = userDetailsHasChanges ? omitEmptyStrings({ ...(user?.userDetails || {}), ...userDetailsUpdated }) : undefined;

    const addressPayload = addressHasChanges ? omitEmptyStrings(addressUpdated) : undefined;

    if (namesPayload || detailsPayload || addressPayload) {
      hasSavedRef.current = true;
      dispatch(
        userActions.updateIndividualCollectData({
          namesPayload,
          detailsPayload,
          addressPayload,
        }),
      );
    } else {
      setShowSummary(true);
    }
  };

  useEffect(() => {
    if (!updatingIndividualCollectData && hasSavedRef.current && !updateIndividualCollectDataError) {
      hasSavedRef.current = false;
      setShowSummary(true);
    }
  }, [updatingIndividualCollectData, updateIndividualCollectDataError]);

  useEffect(() => {
    scrollOnboardingToTop();
  }, [showSummary]);

  useEffect(() => {
    if (hasJustConfirmedRef.current && !advancingOnboardingStep && onboardingStatus?.currentStep && onboardingStatus.currentStep !== 'CollectData') {
      const nextPath = getPostCollectDataNextPath(onboardingStatus?.steps, { userType: user?.userType });
      if (nextPath) {
        hasJustConfirmedRef.current = false;
        navigate(nextPath);
      }
    }
  }, [advancingOnboardingStep, navigate, onboardingStatus?.currentStep, onboardingStatus?.steps, user]);

  if (!user) {
    return null;
  }

  const isSaving = updatingIndividualCollectData;
  const displayError =
    cognitoNamesError ??
    updateIndividualCollectDataError?.message ??
    updateIndividualCollectDataError?.detail ??
    updateUserNamesError?.message ??
    updateUserNamesError?.detail ??
    updateUserError?.message ??
    updateUserError?.detail;

  const handleConfirmData = useCallback(() => {
    const shouldAdvance = onboardingStatus?.currentStep === 'CollectData' && !isCollectDataStepCompleted;
    if (shouldAdvance) {
      hasJustConfirmedRef.current = true;
      dispatch(userActions.advanceOnboardingStep('CollectData'));
      return;
    }
    const nextPath =
      getPostCollectDataNextPath(onboardingStatus?.steps, { userType: user?.userType }) ??
      (onboardingStatus?.currentStep ? getOnboardingStepPath(onboardingStatus.currentStep) : null);
    if (nextPath) navigate(nextPath);
  }, [dispatch, isCollectDataStepCompleted, navigate, onboardingStatus?.currentStep, onboardingStatus?.steps, user]);

  const handleBackToEdit = useCallback(() => {
    setShowSummary(false);
    setSummaryValues(null);
    dispatch(userActions.getUser());
  }, [dispatch]);

  useEffect(() => {
    let continueLabel = 'Continue';
    if (showSummary) {
      continueLabel = advancingOnboardingStep ? 'Confirming…' : 'Confirm';
    } else if (isSaving) {
      continueLabel = 'Saving...';
    }

    setFooterConfig({
      continueLabel,
      continueDisabled: showSummary ? advancingOnboardingStep : isSaving,
      onContinue: showSummary ? handleConfirmData : () => formikRef.current?.submitForm(),
      ...(showSummary
        ? {
            backLabel: 'Back to Edit',
            backDisabled: isLocked,
            onBack: handleBackToEdit,
          }
        : {}),
    });

    return () => setFooterConfig({});
  }, [advancingOnboardingStep, handleBackToEdit, handleConfirmData, isLocked, isSaving, setFooterConfig, showSummary]);

  if (showSummary) {
    const birthDateDisplay = formatBirthDateForDisplay(summaryValues?.birthDate ?? user?.userDetails?.birthDate);
    const countryValue = summaryValues?.country ?? user?.userDetails?.address?.country;
    const countryDisplay = countryOptions.find((country) => country.code === (countryValue || '').toUpperCase())?.name ?? countryValue;
    const line2 = summaryValues?.line2 ?? user?.userDetails?.address?.line2;

    return (
      <ReviewStepShell
        icon={<DocumentReviewIcon />}
        title='Review your details.'
        subtitle='Please confirm before we continue.'
        footer={
          advanceOnboardingStepError ? (
            <ErrorMessage
              error={advanceOnboardingStepError?.message ?? advanceOnboardingStepError?.detail ?? 'Could not advance. Please try again.'}
              className='mt-4'
            />
          ) : null
        }
      >
        <ReviewSection title='Personal details' onEdit={handleBackToEdit}>
          <ReviewRow label='First name' value={summaryValues?.firstName ?? user?.firstName} />
          <ReviewRow label='Middle name' value={(summaryValues?.middleName ?? user?.userDetails?.middleName) || '—'} />
          <ReviewRow label='Last name' value={summaryValues?.lastName ?? user?.lastName} />
          <ReviewRow label='Date of birth' value={birthDateDisplay || '—'} />
        </ReviewSection>

        <ReviewSection title='Contact information' onEdit={handleBackToEdit}>
          <ReviewRow label='Email' value={summaryValues?.email ?? user?.email} />
          <ReviewRow
            label='Phone number'
            value={summaryValues?.phoneNumber ?? user?.phoneNumber ?? user?.userDetails?.phoneNumber ?? '—'}
          />
        </ReviewSection>

        <ReviewSection title='Residential address' isLast onEdit={handleBackToEdit}>
          <ReviewRow label='Street address' value={(summaryValues?.line1 ?? user?.userDetails?.address?.line1) || '—'} />
          {line2 ? <ReviewRow label='Address line 2' value={line2} /> : null}
          <ReviewRow label='City' value={(summaryValues?.city ?? user?.userDetails?.address?.city) || '—'} />
          <ReviewRow
            label='State / Region / Province'
            value={(summaryValues?.stateRegionProvince ?? user?.userDetails?.address?.stateRegionProvince) || '—'}
          />
          <ReviewRow label='Postcode' value={(summaryValues?.postCode ?? user?.userDetails?.address?.postCode) || '—'} />
          <ReviewRow label='Country' value={countryDisplay || '—'} />
        </ReviewSection>
      </ReviewStepShell>
    );
  }

  return (
    <div className='flex w-full justify-center px-4 pb-24 pt-[4vh]'>
      <div className='w-full max-w-[540px]'>
        <div className='animate-[slideInRight_280ms_ease-out_both] rounded-[20px] bg-white px-6 py-7 text-[#0B1929] shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
          <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
            <UserIcon className='h-6 w-6' />
          </div>
          <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Your details.</h1>

          <Formik
            innerRef={formikRef}
            validationSchema={schema}
            initialValues={{
              firstName: '',
              lastName: '',
              middleName: '',
              birthDate: '',
              email: '',
              phoneNumber: '',
              line1: '',
              line2: '',
              city: '',
              stateRegionProvince: '',
              postCode: '',
              country: '',
            }}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={handleSubmit}
          >
            {({ handleChange, submitForm, values, errors, touched, setFieldValue, validateForm }) => (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitForm();
                }}
                className='mt-0'
              >
                <fieldset disabled={isLocked}>
                  <div className={sectionHeaderClass}>
                    <UserIcon className='h-[13px] w-[13px]' />
                    Personal information
                  </div>

                  <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                    <div className={fieldGroupClass}>
                      <label htmlFor='firstName' className={fieldLabelClass}>
                        First name
                      </label>
                      <input
                        id='firstName'
                        type='text'
                        name='firstName'
                        value={values.firstName}
                        onChange={handleChange}
                        className={`${inputClass} ${errors.firstName ? inputErrorBorderClass : ''}`}
                      />
                      {touched.firstName && errors.firstName && <p className={fieldErrorClass}>{errors.firstName}</p>}
                    </div>
                    <div className={fieldGroupClass}>
                      <label htmlFor='lastName' className={fieldLabelClass}>
                        Last name
                      </label>
                      <input
                        id='lastName'
                        type='text'
                        name='lastName'
                        value={values.lastName}
                        onChange={handleChange}
                        className={`${inputClass} ${errors.lastName ? inputErrorBorderClass : ''}`}
                      />
                      {touched.lastName && errors.lastName && <p className={fieldErrorClass}>{errors.lastName}</p>}
                    </div>
                  </div>

                  <button
                    type='button'
                    onClick={() => setShowMiddleName((prev) => !prev)}
                    className='mb-3.5 block w-fit text-left text-[13px] text-[#2E6BAD] hover:underline disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    {showMiddleName ? '- Hide middle name' : '+ Add middle name'}
                  </button>
                  {showMiddleName && (
                    <div className={fieldGroupClass}>
                      <label htmlFor='middleName' className={fieldLabelClass}>
                        Middle name
                      </label>
                      <input id='middleName' type='text' name='middleName' value={values.middleName} onChange={handleChange} className={inputClass} />
                    </div>
                  )}

                  <div className={fieldGroupClass}>
                    <label htmlFor='birthDate' className={fieldLabelClass}>
                      Date of birth
                    </label>
                    <DatePicker
                      id='birthDate'
                      name='birthDate'
                      value={values.birthDate}
                      max={maxBirthDate}
                      onChange={handleChange}
                      className={`${inputClass} ${errors.birthDate ? inputErrorBorderClass : ''}`}
                    />
                    {touched.birthDate && errors.birthDate && <p className={fieldErrorClass}>{errors.birthDate}</p>}
                  </div>

                  <div className={fieldGroupClass}>
                    <div className='mb-2.5 flex items-center justify-between gap-3 rounded-[10px] bg-[#0B1929] px-4 py-3'>
                      <span className='shrink-0 text-[11px] font-bold uppercase tracking-[0.07em] text-white/45'>Email</span>
                      <span className='min-w-0 text-right'>
                        <span className='break-all text-sm font-medium text-white/90'>{values.email || '-'}</span>{' '}
                        <span className='inline-flex items-center rounded-full bg-[#4ade80]/[.12] px-2 py-0.5 text-[11px] font-semibold text-[#4ade80]'>
                          ✓ Verified
                        </span>
                      </span>
                    </div>
                    <div className='flex items-center justify-between gap-3 rounded-[10px] bg-[#0B1929] px-4 py-3'>
                      <span className='shrink-0 text-[11px] font-bold uppercase tracking-[0.07em] text-white/45'>Phone</span>
                      <span className='min-w-0 text-right'>
                        <span className='break-all text-sm font-medium text-white/90'>{values.phoneNumber || '-'}</span>{' '}
                        <span className='inline-flex items-center rounded-full bg-[#4ade80]/[.12] px-2 py-0.5 text-[11px] font-semibold text-[#4ade80]'>
                          ✓ Verified
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className={sectionHeaderClass}>
                    <MapPinIcon className='h-[13px] w-[13px]' />
                    Address
                  </div>

                  <div className={`${fieldGroupClass} relative`}>
                    <label htmlFor='addressSearch' className={fieldLabelClass}>
                      Search your address
                    </label>
                    <PlacesAutocomplete
                      id='addressSearch'
                      value={addressSearch}
                      onChange={setAddressSearch}
                      onAddressSelect={(addressData) => onAddressSelect(addressData, setFieldValue, validateForm)}
                      placeholder='Start typing your address...'
                      helperText='Or fill in manually below.'
                      includedPrimaryTypes={addressAutocompletePrimaryTypes}
                      showSearchIcon={false}
                      className='[&_input]:h-[43px] [&_input]:rounded-[10px] [&_input]:border-[1.5px] [&_input]:border-[#DDE6EF] [&_input]:bg-white [&_input]:px-3.5 [&_input]:py-0 [&_input]:text-sm [&_input]:text-[#0B1929] [&_input]:shadow-none [&_input]:outline-none [&_input]:ring-0 [&_input]:transition [&_input]:focus-visible:border-[#2E6BAD] [&_input]:focus-visible:ring-[3px] [&_input]:focus-visible:ring-[#2E6BAD]/[.12] [&_p]:mt-[5px] [&_p]:text-[11.5px] [&_p]:text-[#7A9AB0]'
                      disabled={isLocked}
                    />
                  </div>

                  <div className={fieldGroupClass}>
                    <label htmlFor='line1' className={fieldLabelClass}>
                      Street address
                    </label>
                    <input
                      id='line1'
                      type='text'
                      name='line1'
                      value={values.line1}
                      onChange={handleChange}
                      className={`${inputClass} ${errors.line1 ? inputErrorBorderClass : ''}`}
                    />
                    {touched.line1 && errors.line1 && <p className={fieldErrorClass}>{errors.line1}</p>}
                  </div>

                  <button
                    type='button'
                    onClick={() => setShowAddressLine2((prev) => !prev)}
                    className='mb-3.5 block w-fit text-left text-[13px] text-[#2E6BAD] hover:underline disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    {showAddressLine2 ? '- Hide line 2' : '+ Add address line 2'}
                  </button>
                  {showAddressLine2 && (
                    <div className={fieldGroupClass}>
                      <label htmlFor='line2' className={fieldLabelClass}>
                        Address line 2 <span className='font-normal normal-case tracking-normal opacity-60'>(optional)</span>
                      </label>
                      <input id='line2' type='text' name='line2' value={values.line2} onChange={handleChange} className={inputClass} />
                    </div>
                  )}

                  <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                    <div className={fieldGroupClass}>
                      <label htmlFor='city' className={fieldLabelClass}>
                        City
                      </label>
                      <input
                        id='city'
                        type='text'
                        name='city'
                        value={values.city}
                        onChange={handleChange}
                        className={`${inputClass} ${errors.city ? inputErrorBorderClass : ''}`}
                      />
                      {touched.city && errors.city && <p className={fieldErrorClass}>{errors.city}</p>}
                    </div>
                    <div className={fieldGroupClass}>
                      <label htmlFor='stateRegionProvince' className={fieldLabelClass}>
                        State / Region / Province
                      </label>
                      <input
                        id='stateRegionProvince'
                        type='text'
                        name='stateRegionProvince'
                        value={values.stateRegionProvince}
                        onChange={handleChange}
                        className={`${inputClass} ${errors.stateRegionProvince ? inputErrorBorderClass : ''}`}
                      />
                      {touched.stateRegionProvince && errors.stateRegionProvince && <p className={fieldErrorClass}>{errors.stateRegionProvince}</p>}
                    </div>
                  </div>

                  <div className={fieldGroupClass}>
                    <label htmlFor='postCode' className={fieldLabelClass}>
                      Postcode
                    </label>
                    <input
                      id='postCode'
                      type='text'
                      name='postCode'
                      value={values.postCode}
                      onChange={handleChange}
                      className={`${inputClass} ${errors.postCode ? inputErrorBorderClass : ''}`}
                    />
                    {touched.postCode && errors.postCode && <p className={fieldErrorClass}>{errors.postCode}</p>}
                  </div>

                  <div className={fieldGroupClass}>
                    <label htmlFor='country' className={fieldLabelClass}>
                      Country of residence
                    </label>
                    <SearchableSelect
                      id='country'
                      name='country'
                      value={values.country}
                      onChange={(event) => setFieldValue('country', event.target.value)}
                      placeholder={countryOptions.length === 0 ? 'Loading countries...' : 'Search country...'}
                      options={countrySelectOptions}
                      showCountryFlags
                      searchPlaceholder='Search country...'
                      menuPlacement='auto'
                      menuMaxHeight={240}
                      menuMinWidth='w-full'
                      clearable
                      hasError={Boolean(touched.country && errors.country)}
                      triggerClassName={searchableSelectTriggerClass(Boolean(touched.country && errors.country))}
                    />
                    {touched.country && errors.country && <p className={fieldErrorClass}>{errors.country}</p>}
                  </div>

                  {displayError && <ErrorMessage error={displayError} />}
                </fieldset>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default CollectDataIndividual;
